import React from 'react';

import * as styles from './styles';
import { PasswordRules } from './PasswordRules';

import { MAX_LENGTH, Rules, validatePassword, type ValidationResponse } from '@cobliteam/password-strength-meter';

import { Caption, DefaultInput, FormControl, Label } from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

export interface NewPasswordProps {
  onPasswordChange: (password: string, valid: boolean) => void;
  password: string;
  onConfirmPasswordChange: (confirmPassword: string, valid: boolean) => void;
  confirmPassword: string;
  darkTheme?: boolean;
  inputsDisabled?: boolean;
  confirmButtonText: string;
  onConfirmButtonClick: () => void;
  confirmButtonDisabled?: boolean;
  confirmButtonLoading?: boolean;
  showCancelButton?: boolean;
  onCancelButtonClick?: () => void;
  passwordPlaceholder?: string;
  confirmPasswordPlaceholder?: string;
  passwordLabel?: string;
  confirmPasswordLabel?: string;
}

export interface NewPasswordState {
  passwordFocused: boolean;
  passwordTouched: boolean;
  confirmPasswordTouched: boolean;
  validationResponse?: ValidationResponse;
  hasTriedToSubmit: boolean;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class NewPassword extends React.Component<NewPasswordProps, NewPasswordState> {
  private initialState = {
    passwordFocused: false,
    passwordTouched: false,
    confirmPasswordTouched: false,
    hasTriedToSubmit: false,
    validationResponse: undefined,
  };

  constructor(props: NewPasswordProps) {
    super(props);
    this.state = this.initialState;
  }

  private setPasswordFocus = (focused: boolean) => this.setState({ passwordFocused: focused });

  private onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.currentTarget.value;
    const validationResponse = validatePassword(password);
    this.setState({ validationResponse, passwordTouched: true });
    if (validationResponse.rules[Rules.noForbiddenChar].valid) {
      this.props.onPasswordChange(password, validationResponse.isPasswordValid);
    }
  };

  private onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const confirmPassword = e.currentTarget.value;
    const isConfirmPasswordValid = confirmPassword === this.props.password;
    this.props.onConfirmPasswordChange(confirmPassword, isConfirmPasswordValid);
  };

  private saveNewPassword = () => {
    this.setState({ hasTriedToSubmit: true }, this.props.onConfirmButtonClick);
  };

  private reset = () => {
    this.setState(this.initialState, this.props.onCancelButtonClick);
  };

  public render() {
    const {
      darkTheme,
      password,
      confirmPassword,
      inputsDisabled,
      confirmButtonDisabled,
      showCancelButton,
      confirmButtonText,
      confirmButtonLoading,
      onCancelButtonClick,
      passwordPlaceholder,
      confirmPasswordPlaceholder,
      passwordLabel,
      confirmPasswordLabel,
    } = this.props;
    const { confirmPasswordTouched, passwordFocused, passwordTouched, validationResponse, hasTriedToSubmit } = this.state;
    const passwordLength = password.length;
    const passwordValid = validationResponse?.isPasswordValid;
    const passwordError = passwordTouched && !passwordValid;
    const confirmPasswordValid = password === confirmPassword;
    const confirmPasswordError = !confirmPasswordValid && confirmPasswordTouched;
    const showConfirmPasswordError = confirmPasswordError && hasTriedToSubmit;
    return (
      <>
        <div className={styles.container}>
          <FormControl disabled={inputsDisabled}>
            <Label className={styles.formLabel(darkTheme)} shrink>
              {passwordLabel || 'Nova senha'}
            </Label>
            <DefaultInput
              type='password'
              onFocus={() => this.setPasswordFocus(true)}
              onBlur={() => this.setPasswordFocus(false)}
              value={password}
              onChange={this.onPasswordChange}
              placeholder={passwordPlaceholder || 'Insira a nova senha'}
              inputProps={{
                maxLength: MAX_LENGTH,
              }}
              error={passwordError && !passwordFocused}
            />
            {(passwordFocused || passwordError) && (
              <>
                <Caption className={styles.passwordCaption(darkTheme)}>
                  <span className={styles.passwordCount}>{passwordLength}</span>
                  {`caracteres${passwordLength >= MAX_LENGTH ? ' (Você alcançou o limite máximo!)' : ''}`}
                </Caption>
                <PasswordRules darkTheme={darkTheme} validationResponse={validationResponse} />
              </>
            )}
          </FormControl>
          <FormControl error={showConfirmPasswordError} className={styles.confirmPasswordPanel} disabled={inputsDisabled}>
            <Label className={styles.formLabel(darkTheme)} shrink>
              {confirmPasswordLabel || 'Confirmar nova senha'}
            </Label>
            <DefaultInput
              type='password'
              onChange={this.onConfirmPasswordChange}
              value={confirmPassword}
              onBlur={() => this.setState({ confirmPasswordTouched: true })}
              placeholder={confirmPasswordPlaceholder || 'Insira a nova senha novamente'}
              inputProps={{
                maxLength: MAX_LENGTH,
              }}
            />
            {showConfirmPasswordError && <Caption>A senha não é idêntica à do campo "Nova senha". Tente novamente.</Caption>}
          </FormControl>
        </div>
        <div className='mt-10 flex gap-2.5'>
          <Button loading={confirmButtonLoading} disabled={confirmButtonDisabled} className={onCancelButtonClick !== undefined ? 'w-40' : 'w-full'} onClick={this.saveNewPassword}>
            {confirmButtonText}
          </Button>
          {showCancelButton ? (
            <Button variant='outlined' className='w-40' onClick={this.reset}>
              Cancelar
            </Button>
          ) : null}
        </div>
      </>
    );
  }
}
